import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment as environmentLogin } from '../../../environments/environment.login';
import { environment as environmentJoin } from '../../../environments/environment.join';
import { environment as environmentDsp } from '../../../environments/environment.dsp';
import { environment as environmentSsp } from '../../../environments/environment.ssp';
import { environment as environmentLoginMataloka } from '../../../environments/environment.login.mataloka';
import { environment as environmentJoinMataloka } from '../../../environments/environment.join.mataloka';
import { environment as environmentDspMataloka } from '../../../environments/environment.dsp.mataloka';
import { environment as environmentSspMataloka } from '../../../environments/environment.ssp.mataloka';
import { environment as environmentLocalhost } from '../../../environments/environment';
import { environment as environmentDevMataloka } from '../../../environments/environment.dev.mataloka';
import { UserRoleType } from '../../shared/interfaces/auth.interfaces';

export interface DomainConfig {
  production: boolean;
  domain: string;
  apiUrl: string;
  API_KEY: string;
  appName: string;
  googleClientId?: string;
  userType?: UserRoleType;
  features: {
    auth: boolean;
    dsp: boolean;
    ssp: boolean;
  };
  allowedRoutes?: string[];
  restrictedRoutes?: string[];
  loginDomain?: string;
  redirectAfterLogin?: {
    dsp: string;
    ssp: string;
  };
  redirectAfterRegistration?: {
    dsp: string;
    ssp: string;
  };
}

export type DomainType = 'login' | 'join' | 'dsp' | 'ssp' | 'localhost' | string;

@Injectable({
  providedIn: 'root'
})
export class DomainService {
  private currentDomainSubject = new BehaviorSubject<DomainType>('localhost');
  private configSubject = new BehaviorSubject<DomainConfig>({
    production: false,
    domain: 'localhost',
    apiUrl: 'http://127.0.0.1:3000/api',
    API_KEY: '',
    appName: 'DigiMax',
    features: {
      auth: true,
      dsp: true,
      ssp: true
    }
  });

  public currentDomain$ = this.currentDomainSubject.asObservable();
  public config$ = this.configSubject.asObservable();

  private clientDashboardLogoUrlSubject = new BehaviorSubject<string | null>(null);
  public clientDashboardLogoUrl$ = this.clientDashboardLogoUrlSubject.asObservable();
  
  private baseDomain = 'admaxhub.com';

  constructor() {
    this.detectDomain();
    const stored = this.safeGetLocalStorage('client_dashboard_logo_url');
    if (stored) {
      this.clientDashboardLogoUrlSubject.next(stored);
    }
  }

  private safeGetLocalStorage(key: string): string | null {
    try {
      const v = localStorage.getItem(key);
      return v && v.trim().length ? v : null;
    } catch {
      return null;
    }
  }

  private safeSetLocalStorage(key: string, value: string | null): void {
    try {
      if (value && value.trim().length) {
        localStorage.setItem(key, value);
      } else {
        localStorage.removeItem(key);
      }
    } catch {
    }
  }

  setClientDashboardLogoUrl(url: string | null): void {
    const next = url && url.trim().length ? url.trim() : null;
    this.clientDashboardLogoUrlSubject.next(next);
    this.safeSetLocalStorage('client_dashboard_logo_url', next);
  }

  getClientDashboardLogoUrl(): string | null {
    return this.clientDashboardLogoUrlSubject.value;
  }

  private isMatalokaDomain(baseDomain: string): boolean {
    if (!baseDomain) return false;
    return baseDomain === 'mataloka.com' || baseDomain.endsWith('.mataloka.com') || baseDomain.includes('mataloka');
  }

  private detectDomain(): void {
    const hostname = window.location.hostname;
    let domainType: DomainType = 'localhost';
    let config: DomainConfig;

    // Check for localhost
    if (hostname === 'localhost' || hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
      domainType = 'localhost';
      config = environmentLocalhost;
      this.baseDomain = 'mataloka.com';
      this.currentDomainSubject.next(domainType);
      this.configSubject.next(config);
      return;
    }

    // Dev environment - treat as localhost (all features, no subdomain split)
    if (hostname === 'dev.mataloka.com') {
      domainType = 'localhost';
      config = { ...environmentDevMataloka };
      this.baseDomain = 'mataloka.com';
      this.currentDomainSubject.next(domainType);
      this.configSubject.next(config);
      return;
    }

    // Dynamic domain detection
    if (hostname.startsWith('login.')) {
      domainType = 'login';
      this.baseDomain = hostname.replace('login.', '');
      config = this.isMatalokaDomain(this.baseDomain) ? { ...environmentLoginMataloka } : { ...environmentLogin };
    } else if (hostname.startsWith('join.')) {
      domainType = 'join';
      this.baseDomain = hostname.replace('join.', '');
      config = this.isMatalokaDomain(this.baseDomain) ? { ...environmentJoinMataloka } : { ...environmentJoin };
    } else if (hostname.startsWith('dsp.')) {
      domainType = 'dsp';
      this.baseDomain = hostname.replace('dsp.', '');
      config = this.isMatalokaDomain(this.baseDomain) ? { ...environmentDspMataloka } : { ...environmentDsp };
    } else if (hostname.startsWith('ssp.')) {
      domainType = 'ssp';
      this.baseDomain = hostname.replace('ssp.', '');
      config = this.isMatalokaDomain(this.baseDomain) ? { ...environmentSspMataloka } : { ...environmentSsp };
    } else {
      // Fallback logic for custom domains (e.g., mataloka.id direct access)
      // Since localhost is already handled at the top, anything reaching here is a non-localhost custom domain
      // Treat it as a valid domain (login/landing)
      
      this.baseDomain = hostname;
      domainType = 'login'; // Default to login-like behavior or generic
      config = this.isMatalokaDomain(this.baseDomain) ? { ...environmentLoginMataloka } : { ...environmentLogin }; // Use login config as base template
    }

    // Update config URLs with dynamic base domain
    if (domainType !== 'localhost') {
      const replaceDomain = (url: string) => {
        if (!url) return url;
        // Replace admaxhub.com with the detected base domain
        return url.replace('admaxhub.com', this.baseDomain);
      };

      config.domain = replaceDomain(config.domain);
      
      if (config.loginDomain) {
        config.loginDomain = replaceDomain(config.loginDomain);
      } else {
        // Ensure loginDomain is set for consistency
        config.loginDomain = `https://login.${this.baseDomain}`;
      }

      if (config.redirectAfterLogin) {
        config.redirectAfterLogin = {
          dsp: replaceDomain(config.redirectAfterLogin.dsp),
          ssp: replaceDomain(config.redirectAfterLogin.ssp)
        };
      }

      if (config.redirectAfterRegistration) {
        config.redirectAfterRegistration = {
          dsp: replaceDomain(config.redirectAfterRegistration.dsp),
          ssp: replaceDomain(config.redirectAfterRegistration.ssp)
        };
      }
    }

    this.currentDomainSubject.next(domainType);
    this.configSubject.next(config);
  }

  getCurrentDomain(): DomainType {
    return this.currentDomainSubject.value;
  }

  getConfig(): DomainConfig {
    return this.configSubject.value;
  }

  isRouteAllowed(route: string, accessLevel?: 'admin' | 'user' | 'in-house' | null): boolean {
    const config = this.getConfig();
    
    // Admin dan in-house dapat mengakses semua route
    if (accessLevel === 'admin' || accessLevel === 'in-house') {
      return true;
    }
    
    // Jika tidak ada restricted routes, semua route diizinkan
    if (!config.restrictedRoutes || config.restrictedRoutes.length === 0) {
      // Cek allowed routes jika ada
      if (config.allowedRoutes && config.allowedRoutes.length > 0) {
        return config.allowedRoutes.some(allowedRoute => 
          route.startsWith(allowedRoute) || allowedRoute === '/'
        );
      }
      return true;
    }

    // Cek apakah route ada di restricted routes
    const isRestricted = config.restrictedRoutes.some(restrictedRoute => 
      route.startsWith(restrictedRoute)
    );

    return !isRestricted;
  }

  getRedirectUrl(userType: UserRoleType): string {
    const config = this.getConfig();
    const currentDomain = this.getCurrentDomain();
    
    // Untuk localhost/development, gunakan path relatif
    if (currentDomain === 'localhost') {
      return '/main';
    }
    
    // Untuk production, gunakan URL absolut
    if (config.redirectAfterLogin) {
      return (userType === 'Advertisers' || userType === 'Operator')
        ? config.redirectAfterLogin.dsp + '/main'
        : config.redirectAfterLogin.ssp + '/main';
    }
    
    if (config.redirectAfterRegistration) {
      return (userType === 'Advertisers' || userType === 'Operator')
        ? config.redirectAfterRegistration.dsp + '/main'
        : config.redirectAfterRegistration.ssp + '/main';
    }

    // Fallback redirect berdasarkan userType
    return (userType === 'Advertisers' || userType === 'Operator')
      ? `https://dsp.${this.baseDomain}/main` 
      : `https://ssp.${this.baseDomain}/main`;
  }

  getLoginUrl(): string {
    const config = this.getConfig();
    if (config.loginDomain) {
      return config.loginDomain;
    }
    return `https://login.${this.baseDomain}`;
  }

  isDSPDomain(): boolean {
    return this.getCurrentDomain() === 'dsp';
  }

  isSSPDomain(): boolean {
    return this.getCurrentDomain() === 'ssp';
  }

  isAuthDomain(): boolean {
    const domain = this.getCurrentDomain();
    return domain === 'login' || domain === 'join';
  }

  getUserType(): UserRoleType | null {
    const config = this.getConfig();
    return config.userType || null;
  }

  // Helper method untuk mencegah redirect loop
  isUserInCorrectDomain(userType: UserRoleType): boolean {
    const currentDomain = this.getCurrentDomain();
    
    // Untuk localhost, semua user type diizinkan
    if (currentDomain === 'localhost') {
      return true;
    }

    if (userType === 'Operator') {
      return true;
    }
    
    // Cek apakah user berada di domain yang sesuai
    if (userType === 'Advertisers' && currentDomain === 'dsp') {
      return true;
    }
    
    if (userType === 'Media Owners' && currentDomain === 'ssp') {
      return true;
    }
    
    return false;
  }

  // Helper method untuk mendapatkan domain yang sesuai dengan user type
  getCorrectDomainForUser(userType: UserRoleType): DomainType {
    if (userType === 'Advertisers') {
      return 'dsp';
    }
    
    if (userType === 'Media Owners') {
      return 'ssp';
    }
    
    // Fallback ke current domain
    return this.getCurrentDomain();
  }

  getDomainLogo(): string {
    // Check if base domain contains mataloka (e.g. mataloka.com, login.mataloka.com)
    if (this.baseDomain && this.baseDomain.includes('mataloka')) {
      return '/mataloka.png';
    }
    return '/admaxhub.png';
  }
}
